/**
 * Format token amounts for display
 * @param {string|number} amount - Amount in wei or ether format
 * @param {number} decimals - Number of decimal places to show
 * @returns {string} Formatted amount
 */
export const formatTokenAmount = (amount, decimals = 4) => {
  if (!amount || amount === '0' || amount === 0) {
    return '0';
  }

  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (numAmount < 0.0001) {
    return '< 0.0001';
  }
  
  if (numAmount < 1) {
    return numAmount.toFixed(decimals);
  }
  
  if (numAmount < 1000) {
    return numAmount.toFixed(decimals > 2 ? 2 : decimals);
  }
  
  if (numAmount < 1000000) {
    return (numAmount / 1000).toFixed(1) + 'K';
  }
  
  return (numAmount / 1000000).toFixed(1) + 'M';
};

/**
 * Format time remaining in a user-friendly way
 * @param {number} seconds - Seconds remaining
 * @returns {string} Formatted time string
 */
export const formatTimeRemaining = (seconds) => {
  if (!seconds || seconds <= 0) {
    return 'Starting Soon';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

/**
 * Format large numbers with appropriate suffixes
 * @param {number} num - Number to format
 * @returns {string} Formatted number string
 */
export const formatLargeNumber = (num) => {
  if (num < 1000) {
    return num.toString();
  }
  
  if (num < 1000000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  
  if (num < 1000000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  
  return (num / 1000000000).toFixed(1) + 'B';
};

/**
 * Format percentage values
 * @param {number} value - Value to format as percentage
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, decimals = 2) => {
  if (!value && value !== 0) return '0%';
  return (value * 100).toFixed(decimals) + '%';
};

/**
 * Format wallet address for display
 * @param {string} address - Wallet address
 * @param {number} startChars - Characters to show at start
 * @param {number} endChars - Characters to show at end
 * @returns {string} Formatted address
 */
export const formatAddress = (address, startChars = 6, endChars = 4) => {
  if (!address) return '';
  if (address.length <= startChars + endChars) return address;
  
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
};

/**
 * Format timestamp to readable date
 * @param {number} timestamp - Unix timestamp
 * @returns {string} Formatted date string
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Unknown';
  
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);
  
  if (diffInHours < 1) {
    const minutes = Math.floor(diffInHours * 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }
  
  if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }
  
  if (diffInHours < 168) { // Less than a week
    const days = Math.floor(diffInHours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
  
  return date.toLocaleDateString();
};

/**
 * Format cycle time for battle cycles
 * @param {number} cycleStart - Cycle start timestamp
 * @param {number} cycleDuration - Duration in seconds (24 hours = 86400)
 * @returns {object} Cycle timing information
 */
export const formatCycleInfo = (cycleStart, cycleDuration = 86400) => {
  const now = Math.floor(Date.now() / 1000);
  const cycleEnd = cycleStart + cycleDuration;
  const timeRemaining = Math.max(0, cycleEnd - now);
  
  const progress = cycleDuration > 0 ? Math.min(100, ((now - cycleStart) / cycleDuration) * 100) : 0;
  
  return {
    timeRemaining,
    progress: Math.max(0, progress),
    isActive: timeRemaining > 0,
    formattedTimeRemaining: formatTimeRemaining(timeRemaining),
    cycleEnd,
    formattedCycleStart: formatTimestamp(cycleStart)
  };
};

/**
 * Format battle statistics
 * @param {object} stats - Statistics object
 * @returns {object} Formatted statistics
 */
export const formatBattleStats = (stats) => {
  return {
    totalBattles: formatLargeNumber(stats.totalBattles || 0),
    totalVolume: formatTokenAmount(stats.totalVolume || '0'),
    totalDividends: formatTokenAmount(stats.totalDividends || '0'),
    activeWarriors: formatLargeNumber(stats.activeWarriors || 0),
    winRate: formatPercentage(stats.winRate || 0),
    averageBet: formatTokenAmount(stats.averageBet || '0')
  };
};

/**
 * Get status color based on value
 * @param {number} value - Numeric value to evaluate
 * @param {array} thresholds - Array of thresholds [low, medium, high]
 * @returns {string} CSS class name for color
 */
export const getStatusColor = (value, thresholds = [0.1, 1, 10]) => {
  if (value >= thresholds[2]) return 'status-high';
  if (value >= thresholds[1]) return 'status-medium';
  if (value >= thresholds[0]) return 'status-low';
  return 'status-zero';
};

/**
 * Format gas price for display
 * @param {string|number} gasPrice - Gas price in gwei
 * @returns {string} Formatted gas price
 */
export const formatGasPrice = (gasPrice) => {
  if (!gasPrice) return '0 gwei';
  
  const price = typeof gasPrice === 'string' ? parseFloat(gasPrice) : gasPrice;
  
  if (price < 1) {
    return (price * 1000).toFixed(0) + ' mwei';
  }
  
  return price.toFixed(1) + ' gwei';
};

/**
 * Calculate and format APY (Annual Percentage Yield)
 * @param {string|number} dailyReturn - Daily return amount
 * @param {string|number} principal - Principal amount
 * @returns {string} Formatted APY percentage
 */
export const formatAPY = (dailyReturn, principal) => {
  if (!dailyReturn || !principal || principal === '0') return '0%';
  
  const daily = typeof dailyReturn === 'string' ? parseFloat(dailyReturn) : dailyReturn;
  const prin = typeof principal === 'string' ? parseFloat(principal) : principal;
  
  if (prin <= 0) return '0%';
  
  const dailyRate = daily / prin;
  const annualRate = Math.pow(1 + dailyRate, 365) - 1;
  
  return formatPercentage(annualRate, 1);
};